import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ScrollProgress from '../components/ScrollProgress';
import { useLanguage } from '../contexts/LanguageContext';
import { useStats } from '../contexts/StatsContext';
import { mockData } from '../data/mock';
import { Award, Heart, Users, Target, Shield, Clock } from 'lucide-react';

const AboutPage = () => {
  const { t, language } = useLanguage();
  const { googleReviews } = useStats();

  const values = [
    {
      icon: Award,
      title: language === 'de' ? 'Qualität' : 'Quality',
      description: language === 'de'
        ? 'Hochwertige Ausbildung nach neuesten medizinischen Standards und Richtlinien.'
        : 'High-quality training according to the latest medical standards and guidelines.',
    },
    {
      icon: Heart,
      title: language === 'de' ? 'Leidenschaft' : 'Passion',
      description: language === 'de'
        ? 'Wir brennen dafür, Menschen das Wissen zu vermitteln, um Leben zu retten.'
        : 'We are passionate about teaching people the knowledge to save lives.',
    },
    {
      icon: Users,
      title: language === 'de' ? 'Teamgeist' : 'Team Spirit',
      description: language === 'de'
        ? 'In unseren Kursen herrscht eine freundliche und unterstützende Atmosphäre.'
        : 'Our courses have a friendly and supportive atmosphere.',
    },
    {
      icon: Target,
      title: language === 'de' ? 'Fokus' : 'Focus',
      description: language === 'de'
        ? 'Praxisnahe Inhalte, die im Ernstfall wirklich zählen und anwendbar sind.'
        : 'Practical content that really matters and is applicable in emergencies.',
    },
    {
      icon: Shield,
      title: language === 'de' ? 'Sicherheit' : 'Safety',
      description: language === 'de'
        ? 'Ihre Sicherheit und die Ihrer Mitmenschen steht bei uns an erster Stelle.'
        : 'Your safety and that of your fellow human beings comes first.',
    },
    {
      icon: Clock,
      title: language === 'de' ? 'Effizienz' : 'Efficiency',
      description: language === 'de'
        ? 'Kompakte Kurse, die in nur einem Tag alles Wichtige vermitteln.'
        : 'Compact courses that teach everything important in just one day.',
    },
  ];

  return (
    <>
      <ScrollProgress />
      <Header />
      <main className="pt-24">
        {/* Hero */}
        <section className="bg-gradient-hero py-20">
          <div className="container-custom">
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">
                {t('about.title')}
              </h1>
              <p className="text-xl text-neutral-600">
                {t('about.subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="py-20 bg-white">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-neutral-900 mb-6">
                  {t('about.mission')}
                </h2>
                <p className="text-lg text-neutral-600 mb-6">
                  {t('about.missionText')}
                </p>
                <p className="text-neutral-600">
                  {language === 'de'
                    ? 'Bei Erste-Hilfe Fokus in Düsseldorf bieten wir offiziell anerkannte Erste-Hilfe-Kurse an, die sowohl für den Führerschein als auch für betriebliche Ersthelfer gültig sind. Unsere Kurse entsprechen den Anforderungen nach §19 FeV und DGUV 304-001.'
                    : 'At Erste-Hilfe Fokus in Düsseldorf, we offer officially recognized first aid courses that are valid for both driving licenses and workplace first aiders. Our courses meet the requirements of §19 FeV and DGUV 304-001.'}
                </p>
              </div>
              <div className="relative">
                <img
                  src={mockData.images.hero}
                  alt="Erste-Hilfe Kurs"
                  className="rounded-2xl shadow-xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-red-600 text-white p-6 rounded-xl shadow-lg">
                  <p className="text-4xl font-bold">{googleReviews}+</p>
                  <p className="text-sm text-red-100">
                    {language === 'de' ? '5-Sterne Bewertungen' : '5-Star Reviews'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 bg-neutral-50">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-neutral-900 mb-4">
                {t('about.values')}
              </h2>
              <p className="text-neutral-600 max-w-2xl mx-auto">
                {language === 'de'
                  ? 'Diese Werte leiten uns bei allem, was wir tun'
                  : 'These values guide us in everything we do'}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {values.map((value, index) => (
                <div
                  key={index}
                  className="bg-white p-6 rounded-2xl shadow-lg border border-neutral-100 card-hover"
                >
                  <div className="w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center mb-4">
                    <value.icon className="w-7 h-7 text-red-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-neutral-900 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-neutral-600">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Certifications */}
        <section className="py-20 bg-white">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-neutral-900 mb-4">
                {language === 'de' ? 'Zertifizierungen & Anerkennung' : 'Certifications & Recognition'}
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-neutral-50 p-6 rounded-xl text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="font-semibold text-neutral-900 mb-2">§19 FeV</h3>
                <p className="text-sm text-neutral-600">
                  {language === 'de'
                    ? 'Anerkannt für alle Führerscheinklassen'
                    : 'Recognized for all driving license classes'}
                </p>
              </div>
              <div className="bg-neutral-50 p-6 rounded-xl text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="font-semibold text-neutral-900 mb-2">DGUV 304-001</h3>
                <p className="text-sm text-neutral-600">
                  {language === 'de'
                    ? 'Gültig für betriebliche Ersthelfer'
                    : 'Valid for workplace first aiders'}
                </p>
              </div>
              <div className="bg-neutral-50 p-6 rounded-xl text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="font-semibold text-neutral-900 mb-2">BG Zulassung</h3>
                <p className="text-sm text-neutral-600">Med1Plus.de - 8.0559</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default AboutPage;
